import { DndContext } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { FormSelect } from 'src/shared/components/form-components';
import { FormTooltip } from 'src/shared/components/form-components/form-tooltip';

import { RigOperationSection } from '../../entities/rig-operations/rig-operation-section.entity';
import { RigOperationStage } from '../../entities/rig-operations/rig-operation-stage.entity';
import { RigOperation } from '../../entities/rig-operations/rig-operation.entity';
import { RigOperationsViewModel } from '../../entities/rig-operations/rig-operations-view-model';
import { StageDropdown } from '../stage-dropdown/stage-dropdown';
import { StageItem } from '../stage-item';

import styles from './rig-operation-item.module.scss';

type Props = {
  // TODO: заменить на интерфейс, в котором будут отсутствовать методы изменения значения полей
  rigOperation: RigOperation;
  isAddToChartButtonVisible: boolean;
  rigOperationsViewModel: RigOperationsViewModel;
  onValueChange(
    fieldId: string,
    value: unknown,
    rigOperation: RigOperation,
    stage?: RigOperationStage,
    section?: RigOperationSection,
  ): void;
  onAddWellToChart(): void;
};

export const RigOperationItem = observer<Props>(function RigOperationItem({
  rigOperation,
  isAddToChartButtonVisible,
  rigOperationsViewModel,
  onValueChange,
  onAddWellToChart,
}) {
  const { t } = useTranslation();

  const {
    isFormDisabled,
    rigOptions,
    brigadeOptions,
    stageTypeOptions,
    validationService,
    addStage,
    addSection,
    deleteStage,
    deleteSection,
    isRigOperationFieldBlocked,
    isStageFieldBlocked,
    isSectionFieldBlocked,
    isStageFieldVisible,
    getDefaultTime,
    getDisabledTime,
    setDirectoryValue,
    moveStage,
    moveSection,
    getValidSelectValue,
  } = rigOperationsViewModel;

  return (
    <div className={styles.rigOperationWrapper}>
      <div className={styles.fieldsWrapper}>
        <div className={styles.fieldWrapper}>
          <p className={styles.fieldLabel}>{t('rigOperations:labels.rigId')}</p>
          <FormTooltip text={rigOperation.errors.rigId}>
            <FormSelect
              value={getValidSelectValue('rigId', rigOperation.rigId)}
              options={rigOptions.actual}
              isError={!!rigOperation.errors.rigId}
              isDisabled={isRigOperationFieldBlocked('rigId', rigOperation)}
              onChange={(value) => onValueChange('rigId', value ?? null, rigOperation)}
              onBlur={() => validationService.validateRigId(rigOperation)}
              onFocus={() => rigOperation.removeError('rigId')}
            />
          </FormTooltip>
        </div>
        <div className={styles.fieldWrapper}>
          <p className={styles.fieldLabel}>{t('rigOperations:labels.brigadeId')}</p>
          <FormSelect
            value={getValidSelectValue('brigadeId', rigOperation.brigadeId)}
            options={brigadeOptions.actual}
            isDisabled={isRigOperationFieldBlocked('brigadeId', rigOperation)}
            onChange={(value) => onValueChange('brigadeId', value ?? null, rigOperation)}
          />
        </div>
        {/* Временно отключенный функционал */}
        {/* {isAddToChartButtonVisible && (
          <Button
            variant="primary"
            prefixIcon={<CalendarIcon stroke="white" />}
            disabled={isFormDisabled}
            onClick={onAddWellToChart}
          >
            {t('newWellForm:Approaches.addToChart')}
          </Button>
        )} */}
      </div>

      <div>
        <DndContext
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          onDragEnd={(event) => moveStage(rigOperation, event)}
        >
          <SortableContext
            items={rigOperation.stages.map((stage) => stage.entityId)}
            strategy={verticalListSortingStrategy}
          >
            {rigOperation.stages.map((stage) => {
              return (
                <StageItem
                  key={stage.entityId}
                  stage={stage}
                  // Временный проп для избежания прокидывания большого кол-ва обработчиков валидации
                  rigOperation={rigOperation}
                  rigOperationsViewModel={rigOperationsViewModel}
                  // TODO: Избавиться от этих пропов путём указания родительских айдишников в сущности
                  onValueChange={(fieldId, value, section) => {
                    onValueChange(fieldId, value, rigOperation, stage, section);
                  }}
                  onAddSection={(sectionTypeId: number) => addSection(rigOperation, stage, sectionTypeId)}
                  onDeleteStage={() => deleteStage(rigOperation, stage)}
                  onDeleteSection={(section: RigOperationSection) => deleteSection(rigOperation, stage, section)}
                  isFieldBlocked={(fieldId: string, section?: RigOperationSection): boolean => {
                    if (section) {
                      return isSectionFieldBlocked(fieldId, rigOperation, stage, section);
                    }

                    return isStageFieldBlocked(fieldId, rigOperation, stage);
                  }}
                  isFieldVisible={(fieldId: string, section?: RigOperationSection): boolean => {
                    if (section) {
                      return true;
                    }

                    return isStageFieldVisible(fieldId, rigOperation, stage);
                  }}
                  getDefaultTime={(fieldId: 'startDate' | 'endDate', section?: RigOperationSection) =>
                    getDefaultTime(fieldId, rigOperation, stage, section)
                  }
                  getDisabledTime={(fieldId: 'startDate' | 'endDate', section?: RigOperationSection) =>
                    getDisabledTime(fieldId, rigOperation, stage, section)
                  }
                  setDirectoryValue={(fieldId, section) => setDirectoryValue(fieldId, rigOperation, stage, section)}
                  moveSection={(event) => moveSection(rigOperation, stage, event)}
                />
              );
            })}
          </SortableContext>
        </DndContext>
      </div>

      <StageDropdown
        options={stageTypeOptions.actual}
        isDisabled={isFormDisabled}
        addNewStage={(value) => addStage(rigOperation, value)}
      />
    </div>
  );
});
